import {
  ApiFehler,
  ApiZeitueberschreitung,
  type Empfaenger,
  type KassenApi,
  type Vorgang,
} from './api';
import { SchluesselBelegt, type GeraeteSpeicher, type SpeicherTx } from './geraetespeicher';
import { formatiereEuro, summeBrutto, type Position } from './warenkorb';

/**
 * Offline-Puffer der Kassen-App (CLAUDE.md „Offline"): Ist die API nicht
 * erreichbar, werden Barverkäufe lokal gespeichert und als vorläufige Belege
 * mit dem Pflichthinweis „Sicherheitseinrichtung ausgefallen" ausgegeben.
 * Sobald die Verbindung zurück ist, synchronisiert die App sie in
 * ursprünglicher Reihenfolge; die API hängt sie als Ausfallbelege mit ihrem
 * Originalzeitpunkt in die Kette, protokolliert den Ausfall und schließt mit
 * einem signierten Sammelbeleg ab. Kartenzahlungen sind offline nicht möglich.
 */
export interface OfflineBeleg {
  kassenId: string;
  belegnummer: string;
  /** ISO-8601, Zeitpunkt des Verkaufs auf dem Gerät. */
  zeitpunkt: string;
  positionen: Position[];
  zahlungsart: 'bar';
  empfaenger?: Empfaenger;
  notiz?: string;
  bediener?: string;
  /** Sicherheitspaket S3: übernommener Kunde (automatischer Kundenrabatt). */
  kundeId?: number;
  /** Sicherheitspaket S3: Bedieneranmeldung beim Verkauf (Nachweis des Rabattrechts). */
  bedienerSitzung?: string;
  /** Bar gegebener Betrag (Teil der Nutzdaten des Vorgangs). */
  gegebenCent?: number;
  /** Trinkgeld (Teil der Nutzdaten des Vorgangs). */
  trinkgeldCent?: number;
  /**
   * N1: Vorgangskennung – dieselbe wie beim Online-Versuch. Fehlt sie (Altbestand), vergibt die
   * Synchronisation vor dem ersten Senden eine.
   */
  vorgang?: Vorgang;
}

/** N1.7: Die Synchronisationssperre (Lease) gilt nicht mehr – der Lauf ändert nichts mehr. */
export class LeaseVerloren extends Error {
  constructor() {
    super('Synchronisationssperre abgelaufen bzw. übernommen');
  }
}

/** N1.8: Unter dieser Nummer liegt schon ein Eintrag – nie überschreiben. */
export class PufferNummerBelegt extends Error {
  constructor(belegnummer: string) {
    super(`Offline-Puffer: Nummer ${belegnummer} ist bereits belegt`);
  }
}

/** N1.7: installationsweite Synchronisationssperre ohne Web Locks. */
export interface PufferLease {
  kassenId: string;
  besitzer: string;
  generation: number;
}

/** Ablauf der Lease (N1.7). */
export const LEASE_DAUER_MS = 60_000;

export interface OfflinePuffer {
  /**
   * P1-14 (Codex C3): true = übersteht Neuladen und Neustart (IndexedDB). Ein flüchtiger Puffer
   * (Arbeitsspeicher) darf nie als Offline-Ablage für Verkäufe dienen.
   */
  readonly dauerhaft: boolean;
  alle(kassenId: string): Promise<OfflineBeleg[]>;
  /** N1.8: legt an, überschreibt nie (`PufferNummerBelegt`). */
  speichere(beleg: OfflineBeleg): Promise<void>;
  /** Mit `lease`: nur, solange sie gilt (sonst `LeaseVerloren`, nichts geändert). */
  entferne(kassenId: string, belegnummern: readonly string[], lease?: PufferLease): Promise<void>;
  /** Eintrag unter derselben Nummer ergänzen (Altbestand bekommt eine Vorgangskennung). */
  ergaenze(beleg: OfflineBeleg, lease?: PufferLease): Promise<void>;
  /**
   * N1.1: Vorgangsdatensatz atomar in den Puffer übernehmen (Eintrag anlegen, Datensatz
   * entfernen). Fehlt das, legt die Kasse nur den Eintrag an.
   */
  uebernimmVorgang?(beleg: OfflineBeleg): Promise<void>;
  /**
   * N1.7: Ausfallabschluss-Merker im geschützten Speicher (statt localStorage). Fehlt das, nutzt
   * die Synchronisation den übergebenen Merker.
   */
  abschluss?: {
    lies(kassenId: string): Promise<string | null>;
    setze(kassenId: string, wert: string, lease?: PufferLease): Promise<void>;
  };
  /** N1.7: Lease (nur nötig ohne Web Locks). */
  lease?: {
    /** null = ein anderes Fenster hält eine gültige Lease. */
    erwirb(kassenId: string, besitzer: string, jetzt: number): Promise<PufferLease | null>;
    /** Verlängern; `LeaseVerloren`, wenn sie abgelaufen bzw. übernommen ist. */
    verlaengere(lease: PufferLease, jetzt: number): Promise<void>;
    gibFrei(lease: PufferLease): Promise<void>;
  };
}

/**
 * Puffer im Arbeitsspeicher (Tests, Browser ohne IndexedDB). Flüchtig: Die Kasse nutzt ihn
 * nicht als Offline-Ablage für Verkäufe (P1-14), nur zum Zählen bzw. Synchronisieren.
 */
export class SpeicherPuffer implements OfflinePuffer {
  readonly dauerhaft: boolean = false;
  private readonly belege: OfflineBeleg[] = [];

  alle(kassenId: string): Promise<OfflineBeleg[]> {
    return Promise.resolve(this.belege.filter((b) => b.kassenId === kassenId));
  }

  speichere(beleg: OfflineBeleg): Promise<void> {
    if (
      this.belege.some((b) => b.kassenId === beleg.kassenId && b.belegnummer === beleg.belegnummer)
    ) {
      return Promise.reject(new PufferNummerBelegt(beleg.belegnummer));
    }
    this.belege.push(beleg);
    return Promise.resolve();
  }

  entferne(kassenId: string, belegnummern: readonly string[]): Promise<void> {
    for (let i = this.belege.length - 1; i >= 0; i--) {
      const b = this.belege[i];
      if (b?.kassenId === kassenId && belegnummern.includes(b.belegnummer)) {
        this.belege.splice(i, 1);
      }
    }
    return Promise.resolve();
  }

  ergaenze(beleg: OfflineBeleg): Promise<void> {
    const i = this.belege.findIndex(
      (b) => b.kassenId === beleg.kassenId && b.belegnummer === beleg.belegnummer,
    );
    if (i >= 0) {
      this.belege.splice(i, 1, beleg);
    }
    return Promise.resolve();
  }
}

function pufferSchluessel(kassenId: string, belegnummer: string): string {
  return `${kassenId}|${belegnummer}`;
}

interface LeaseEintrag {
  besitzer: string;
  generation: number;
  ablauf: number;
}

/**
 * N1 (Paketplan v4.1, Punkte 6–8): Offline-Puffer im Gerätespeicher (IndexedDB `bilanx-kassa`
 * v2, Speicher `offline-belege` wie bisher – Bestandseinträge bleiben erhalten). Anlegen mit
 * `add` (nie überschreiben), Lease mit Generation für Browser ohne Web Locks, Abschlussmerker in
 * derselben Datenbank. Jede Mutation unter einer Lease prüft sie in derselben Transaktion.
 */
/** Der Vorgang ist schon abgeschlossen – er wird nicht (mehr) gepuffert. */
export class VorgangSchonAbgeschlossen extends Error {
  constructor(belegnummer: string) {
    super(`Verkauf ${belegnummer} ist bereits abgeschlossen – „Beleg prüfen“`);
  }
}

export class GeraetePuffer implements OfflinePuffer {
  readonly dauerhaft: boolean;

  constructor(
    private readonly speicher: GeraeteSpeicher,
    private readonly uhr: () => number = Date.now,
  ) {
    // Der localStorage-Rückfall puffert nie (N1.6): nicht über Fenster atomar
    this.dauerhaft = speicher.art === 'indexeddb';
  }

  private static async pruefeLease(tx: SpeicherTx, lease: PufferLease | undefined, jetzt: number) {
    if (lease === undefined) {
      return;
    }
    const e = await tx.get<LeaseEintrag>('meta', `lease|${lease.kassenId}`);
    if (e?.besitzer !== lease.besitzer || e.generation !== lease.generation || e.ablauf <= jetzt) {
      throw new LeaseVerloren();
    }
  }

  async alle(kassenId: string): Promise<OfflineBeleg[]> {
    const alle = await this.speicher.transaktion((tx) => tx.alle<OfflineBeleg>('puffer'));
    return alle
      .filter((b) => b.kassenId === kassenId)
      .sort((a, b) => a.zeitpunkt.localeCompare(b.zeitpunkt));
  }

  async speichere(beleg: OfflineBeleg): Promise<void> {
    try {
      await this.speicher.transaktion((tx) =>
        tx.add('puffer', pufferSchluessel(beleg.kassenId, beleg.belegnummer), beleg),
      );
    } catch (e: unknown) {
      if (e instanceof SchluesselBelegt) {
        throw new PufferNummerBelegt(beleg.belegnummer);
      }
      throw e;
    }
  }

  /**
   * N1.1: Vorgangsdatensatz in den Puffer übernehmen – Eintrag anlegen und Datensatz entfernen in
   * einer Transaktion (kein Verlust, kein Doppel).
   */
  async uebernimmVorgang(beleg: OfflineBeleg): Promise<void> {
    try {
      await this.speicher.transaktion(async (tx) => {
        const k = pufferSchluessel(beleg.kassenId, beleg.belegnummer);
        const d = await tx.get<{ vorgang?: { id?: string }; abgeschlossen?: unknown }>(
          'vorgang',
          k,
        );
        // Codex-Bestätigungsprüfung F3: Ein inzwischen abgeschlossener Vorgang (gebucht, ersetzt,
        // nicht gebucht – z. B. aus einem anderen Fenster) wird nie gepuffert und sein
        // Abschlussnachweis nie überschrieben; alles in derselben Transaktion
        if (d?.vorgang?.id === beleg.vorgang?.id && d?.abgeschlossen !== undefined) {
          throw new VorgangSchonAbgeschlossen(beleg.belegnummer);
        }
        await tx.add('puffer', k, beleg);
        // Codex N4-1: Datensatz bleibt als Abschlussnachweis „gepuffert“ (nicht gelöscht)
        // nur genau dieser Vorgang (nie ein fremder Datensatz gleicher Nummer)
        if (d !== undefined && d.vorgang?.id === beleg.vorgang?.id) {
          await tx.put('vorgang', k, {
            ...d,
            abgeschlossen: { art: 'gepuffert', am: new Date().toISOString() },
          });
        }
      });
    } catch (e: unknown) {
      if (e instanceof SchluesselBelegt) {
        throw new PufferNummerBelegt(beleg.belegnummer);
      }
      throw e;
    }
  }

  async entferne(
    kassenId: string,
    belegnummern: readonly string[],
    lease?: PufferLease,
  ): Promise<void> {
    await this.speicher.transaktion(async (tx) => {
      await GeraetePuffer.pruefeLease(tx, lease, this.uhr());
      for (const n of belegnummern) {
        await tx.delete('puffer', pufferSchluessel(kassenId, n));
      }
    });
  }

  async ergaenze(beleg: OfflineBeleg, lease?: PufferLease): Promise<void> {
    await this.speicher.transaktion(async (tx) => {
      await GeraetePuffer.pruefeLease(tx, lease, this.uhr());
      const k = pufferSchluessel(beleg.kassenId, beleg.belegnummer);
      // Nur einen vorhandenen Eintrag ergänzen – nie einen neuen anlegen
      if ((await tx.get('puffer', k)) !== undefined) {
        await tx.put('puffer', k, beleg);
      }
    });
  }

  readonly abschluss = {
    lies: (kassenId: string): Promise<string | null> =>
      this.speicher.transaktion(
        async (tx) => (await tx.get<string>('meta', `abschluss|${kassenId}`)) ?? null,
      ),
    setze: (kassenId: string, wert: string, lease?: PufferLease): Promise<void> =>
      this.speicher.transaktion(async (tx) => {
        await GeraetePuffer.pruefeLease(tx, lease, this.uhr());
        if (wert === '') {
          await tx.delete('meta', `abschluss|${kassenId}`);
        } else {
          await tx.put('meta', `abschluss|${kassenId}`, wert);
        }
      }),
  };

  readonly lease = {
    erwirb: (kassenId: string, besitzer: string, jetzt: number): Promise<PufferLease | null> =>
      this.speicher.transaktion(async (tx) => {
        const e = await tx.get<LeaseEintrag>('meta', `lease|${kassenId}`);
        if (e !== undefined && e.ablauf > jetzt && e.besitzer !== besitzer) {
          return null;
        }
        // Übernahme erst nach Ablauf; jede Vergabe erhöht die Generation
        const generation = (e?.generation ?? 0) + 1;
        await tx.put('meta', `lease|${kassenId}`, {
          besitzer,
          generation,
          ablauf: jetzt + LEASE_DAUER_MS,
        } satisfies LeaseEintrag);
        return { kassenId, besitzer, generation };
      }),
    verlaengere: (lease: PufferLease, jetzt: number): Promise<void> =>
      this.speicher.transaktion(async (tx) => {
        await GeraetePuffer.pruefeLease(tx, lease, jetzt);
        await tx.put('meta', `lease|${lease.kassenId}`, {
          besitzer: lease.besitzer,
          generation: lease.generation,
          ablauf: jetzt + LEASE_DAUER_MS,
        } satisfies LeaseEintrag);
      }),
    gibFrei: (lease: PufferLease): Promise<void> =>
      this.speicher.transaktion(async (tx) => {
        const e = await tx.get<LeaseEintrag>('meta', `lease|${lease.kassenId}`);
        // Nur die eigene, noch gültige Generation freigeben (Generation bleibt erhalten)
        if (e?.besitzer === lease.besitzer && e.generation === lease.generation) {
          await tx.put('meta', `lease|${lease.kassenId}`, { ...e, ablauf: 0 });
        }
      }),
  };
}

/** Netz-/Serverstörung (API nicht erreichbar) – im Unterschied zu fachlichen Ablehnungen. */
export function istNetzfehler(e: unknown): boolean {
  if (e instanceof ApiFehler) {
    return e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504;
  }
  return e instanceof TypeError; // fetch: "Failed to fetch"
}

/**
 * Darf ein gescheiterter Barverkauf in den Offline-Puffer?
 *
 * **Ohne Schalter** (`erweitert` = false, Vorgabe – Codex 1): keine zusätzlichen Auslöser – die
 * Fehlerentscheidung entspricht `istNetzfehler` am Stand 58a387a. (Strenger als 58a387a ist der
 * Verkauf zusätzlich über `offlinePufferBereit`: ohne dauerhaften Puffer bzw. Bonkopf kein
 * Puffer.) Ausgenommen sind nur die neuen Fehlerbilder, die es damals nicht
 * gab: eine Zeitüberschreitung (früher wartete die Kasse bis zum Netzfehler) und ein
 * Infrastruktur-503 (früher 500). Beide führen in den bestehenden Klärungsweg („Beleg prüfen“,
 * die Nummer bleibt reserviert) statt in den Puffer. Ebenso – strenger als 58a387a – ein
 * „klärungsbedürftiges“ 503 (Speicherergebnis unklar, Gutschein-Klärung).
 *
 * **Mit Schalter** (P1-5/P1-8): zusätzlich Zeitüberschreitung und Infrastruktur-503; dafür nur
 * gekennzeichnete 503 (Infrastruktur, Signaturausfall, Entwicklungs-Proxy) bzw. 503 ohne JSON
 * (Reverse-Proxy) – „klärungsbedürftige“ 503 nicht.
 */
export function istPuffergeeignet(e: unknown, erweitert: boolean): boolean {
  if (!istNetzfehler(e)) {
    return false;
  }
  if (!erweitert) {
    return !(
      e instanceof ApiZeitueberschreitung ||
      (e instanceof ApiFehler &&
        e.status === 503 &&
        // Abnahmevorbereitung 7d858ff: „Speicherergebnis unklar“ bzw. Gutschein-Klärung sagt
        // ausdrücklich „Beleg prüfen, nicht neu kassieren“ – nie als Netzausfall puffern.
        (e.daten?.infrastruktur === true || e.daten?.klaerungsbeduerftig === true))
    );
  }
  if (!(e instanceof ApiFehler) || e.status !== 503) {
    return true;
  }
  const d = e.daten;
  if (d === null) {
    return true;
  }
  if (d.klaerungsbeduerftig === true) {
    return false;
  }
  return (
    d.infrastruktur === true ||
    d.signaturdienst === true ||
    d.netz === true ||
    // ältere API: Signaturausfall nur am Hinweis erkennbar
    (typeof d.hinweis === 'string' && d.hinweis.includes('Ausfall der Sicherheitseinrichtung'))
  );
}

/** Unternehmerangaben für den Bonkopf (§ 132a Abs. 3 Z 1 BAO). */
export interface Bonkopf {
  name: string;
  anschrift: string;
  uid?: string;
}

interface Ablage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

function bonkopfSchluessel(kassenId: string): string {
  return `bilanx.bonkopf.${kassenId}`;
}

/**
 * P1-3 (O2-L2): Bonkopf je Kasse dauerhaft sichern (aus `GET …/status`), damit ein vorläufiger
 * Beleg auch nach einem Neustart ohne Server die Unternehmerangaben trägt. null entfernt ihn.
 * false = Schreiben gescheitert (Codex 7: im Gerätestatus melden; ohne gesicherten Kopf kein
 * Puffer).
 */
export function speichereBonkopf(ablage: Ablage, kassenId: string, kopf: Bonkopf | null): boolean {
  try {
    if (kopf === null) {
      ablage.removeItem(bonkopfSchluessel(kassenId));
    } else {
      ablage.setItem(
        bonkopfSchluessel(kassenId),
        JSON.stringify({
          name: kopf.name,
          anschrift: kopf.anschrift,
          ...(kopf.uid === undefined ? {} : { uid: kopf.uid }),
        }),
      );
    }
    return true;
  } catch {
    // Speicher voll bzw. gesperrt: ohne gesicherten Kopf puffert die Kasse nicht (P1-3)
    return false;
  }
}

/** Gesicherter Bonkopf der Kasse oder null (fehlt, unlesbar oder unvollständig). */
export function ladeBonkopf(ablage: Pick<Ablage, 'getItem'>, kassenId: string): Bonkopf | null {
  try {
    const roh = ablage.getItem(bonkopfSchluessel(kassenId));
    if (roh === null || roh === '') {
      return null;
    }
    const w = JSON.parse(roh) as Partial<Record<keyof Bonkopf, unknown>>;
    if (
      typeof w.name !== 'string' ||
      w.name.trim() === '' ||
      typeof w.anschrift !== 'string' ||
      w.anschrift.trim() === ''
    ) {
      return null;
    }
    return {
      name: w.name,
      anschrift: w.anschrift,
      ...(typeof w.uid === 'string' && w.uid !== '' ? { uid: w.uid } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Darf die Kasse jetzt überhaupt offline puffern? Nur mit dauerhaftem Puffer (P1-14) und einem
 * **dauerhaft gesicherten**, vollständigen Bonkopf (P1-3, Codex 7: aus der Ablage gelesen, nicht
 * aus dem Anzeigezustand). Sonst scheitert der Verkauf wie ein Netzfehler ohne Puffer
 * (bestehende Fehlermeldung, offener Beleg bleibt zur Klärung) – keine neue Oberfläche.
 */
export function offlinePufferBereit(
  puffer: OfflinePuffer,
  ablage: Pick<Ablage, 'getItem'>,
  kassenId: string,
  /**
   * N1.11: vom Browser zugesicherte Dauerhaftigkeit (navigator.storage.persist). Fehlt sie (z. B.
   * privates Fenster, Speicher darf geräumt werden), puffert die Kasse nicht – keine Heuristik,
   * nur die tatsächlich gemeldete Fähigkeit.
   */
  speicherDauerhaft = true,
): boolean {
  return speicherDauerhaft && puffer.dauerhaft && ladeBonkopf(ablage, kassenId) !== null;
}

/** Vorläufiger Beleg für den Ausdruck, solange die Kasse nicht erreichbar ist. */
export function offlineBelegText(
  beleg: OfflineBeleg,
  unternehmer: { name: string; anschrift: string; uid?: string } | null,
): string[] {
  const zeit = new Date(beleg.zeitpunkt).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' });
  const zeilen = [
    ...(unternehmer === null
      ? []
      : [
          unternehmer.name,
          unternehmer.anschrift,
          ...(unternehmer.uid === undefined ? [] : [`UID: ${unternehmer.uid}`]),
        ]),
    '------------------------------------------',
    `Kassen-ID: ${beleg.kassenId}`,
    `Belegnummer: ${beleg.belegnummer}`,
    `Datum: ${zeit}`,
    ...(beleg.empfaenger === undefined
      ? []
      : [
          '------------------------------------------',
          'Empfänger:',
          beleg.empfaenger.name,
          beleg.empfaenger.anschrift,
          ...(beleg.empfaenger.uid === undefined ? [] : [`UID: ${beleg.empfaenger.uid}`]),
        ]),
    '------------------------------------------',
  ];
  for (const p of beleg.positionen) {
    const links = `${String(p.menge)} x ${p.bezeichnung}`;
    const rechts = `${formatiereEuro(p.bruttoCent)} (${p.steuersatz} %)`;
    zeilen.push(`${links.padEnd(Math.max(1, 42 - rechts.length))}${rechts}`.slice(0, 60));
  }
  zeilen.push('------------------------------------------');
  const summe = `SUMME BRUTTO EUR`;
  const betrag = formatiereEuro(summeBrutto(beleg.positionen));
  zeilen.push(`${summe.padEnd(42 - betrag.length)}${betrag}`);
  if (beleg.bediener !== undefined && beleg.bediener.trim() !== '') {
    zeilen.push(`Bediener: ${beleg.bediener.trim()}`);
  }
  if (beleg.notiz !== undefined && beleg.notiz.trim() !== '') {
    zeilen.push(`Notiz: ${beleg.notiz.trim()}`);
  }
  zeilen.push('Zahlung: bar');
  zeilen.push('------------------------------------------');
  zeilen.push('*** SICHERHEITSEINRICHTUNG AUSGEFALLEN ***');
  zeilen.push('Vorläufiger Beleg – wird nach Wiederverbindung');
  zeilen.push('mit der Kasse nachsigniert.');
  return zeilen;
}

export interface SyncErgebnis {
  synchronisiert: string[];
  fehler: { belegnummer: string; fehler: string }[];
  ausfallBeendet: boolean;
  /**
   * N1: Einträge in Klärung (Nummer am Server vergeben, nicht sicher derselbe Vorgang). Sie
   * bleiben im Puffer, bis im Backoffice entschieden ist – nie automatisch neu nummeriert.
   */
  inKlaerung: string[];
  /** N1: offene Klärungsfälle der Kasse (halten den Ausfallabschluss an). */
  klaerungenOffen: number;
  /** Rechnungswunsch nicht erfüllbar: der Server hat den Kassenbeleg ohne Empfänger signiert. */
  ohneRechnung: { belegnummer: string; hinweis: string }[];
}

export interface SyncOptionen {
  /**
   * Merker für einen noch offenen Ausfallabschluss (bisher localStorage). Ein Puffer mit eigenem
   * Abschlussmerker (N1.7) liest ihn nur noch zur Übernahme.
   */
  merker?: { getItem(k: string): string | null; setItem(k: string, v: string): void };
  /** P1-1: Belege je Anfrage (Standard SYNC_STAPEL). */
  stapel?: number;
  /** N1.7: Kennung dieses Fensters für die Lease (Standard: zufällig je Lauf). */
  besitzer?: string;
  /** Uhr für die Lease (Tests). */
  uhr?: () => number;
  /** Erzeugt Vorgangs-IDs für Altbestand (Tests). */
  neueVorgangsId?: () => string;
}

/**
 * P1-1: Belege je Synchronisationsanfrage. Klein genug für jedes Körperlimit (der Server nimmt
 * höchstens 500 Belege bzw. 8 MiB je Anfrage), groß genug für einen zügigen Abbau des Puffers.
 */
export const SYNC_STAPEL = 50;

function abschlussSchluessel(kassenId: string): string {
  return `bilanx.ausfallabschluss.${kassenId}`;
}

/**
 * Offener Ausfallabschluss: der Ausfall, den der Vorlauf offen ließ, und die Nummer des
 * Sammelbelegs, unter der wiederholt wird (P1-12). Ältere Merker ('1') kennen beides nicht.
 */
interface OffenerAbschluss {
  ausfallId: number | null;
  sammelbelegnummer: string | null;
  /** N1: Kennung des Nachsendelaufs (bleibt über Unterbrechungen gleich). */
  lauf: string | null;
}

function parseAbschluss(roh: string | null): OffenerAbschluss | null {
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const w = JSON.parse(roh) as unknown;
    if (typeof w === 'object' && w !== null) {
      const o = w as Record<string, unknown>;
      return {
        ausfallId:
          typeof o.ausfallId === 'number' && Number.isInteger(o.ausfallId) && o.ausfallId > 0
            ? o.ausfallId
            : null,
        sammelbelegnummer:
          typeof o.sammelbelegnummer === 'string' && o.sammelbelegnummer !== ''
            ? o.sammelbelegnummer
            : null,
        lauf: typeof o.lauf === 'string' && o.lauf !== '' ? o.lauf : null,
      };
    }
  } catch {
    // älterer Merker ('1')
  }
  return { ausfallId: null, sammelbelegnummer: null, lauf: null };
}

/**
 * Merker lesen: zuerst der geschützte Merker des Puffers (N1.7), sonst der bisherige
 * localStorage-Merker (Übernahme nach dem Update – er wird nicht mehr geschrieben).
 */
async function leseAbschlussRoh(
  puffer: OfflinePuffer,
  merker: SyncOptionen['merker'],
  kassenId: string,
): Promise<string | null> {
  if (puffer.abschluss !== undefined) {
    const geschuetzt = await puffer.abschluss.lies(kassenId);
    if (geschuetzt !== null && geschuetzt !== '') {
      return geschuetzt;
    }
  }
  return merker?.getItem(abschlussSchluessel(kassenId)) ?? null;
}

/** Steht für die Kasse noch ein Sammelbeleg (Ausfallabschluss) aus? */
export async function abschlussOffen(
  puffer: OfflinePuffer,
  merker: SyncOptionen['merker'],
  kassenId: string,
): Promise<boolean> {
  return parseAbschluss(await leseAbschlussRoh(puffer, merker, kassenId)) !== null;
}

/** ID des Ausfalls aus der Serverantwort (null = keiner bzw. unbekannt). */
function ausfallIdAus(ausfall: unknown): number | null {
  if (typeof ausfall === 'object' && ausfall !== null) {
    const id = (ausfall as Record<string, unknown>).id;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
  }
  return null;
}

/** Laufende Synchronisation je Kasse in diesem Tab (Rückfall ohne Web Locks und ohne Lease). */
const laufend = new Map<string, Promise<unknown>>();

function zufallsId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * Höchstens eine Synchronisation je Kasse gleichzeitig – über alle Fenster:
 * - mit Web Locks wie bisher (die Sperre gilt für die ganze Installation);
 * - ohne Web Locks über die Lease des Puffers (N1.7): ein anderes Fenster mit gültiger Lease →
 *   dieser Lauf entfällt (null); jede Puffermutation prüft Generation und Ablauf;
 * - nur ohne beides tablokal (flüchtiger Puffer, puffert ohnehin nicht).
 */
async function exklusiv<T>(
  kassenId: string,
  puffer: OfflinePuffer,
  optionen: SyncOptionen,
  lauf: (lease: PufferLease | undefined) => Promise<T>,
): Promise<T | null> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks !== undefined) {
    return locks.request(`bilanx.sync.${kassenId}`, () => lauf(undefined));
  }
  if (puffer.lease !== undefined) {
    const uhr = optionen.uhr ?? Date.now;
    const lease = await puffer.lease.erwirb(kassenId, optionen.besitzer ?? zufallsId(), uhr());
    if (lease === null) {
      return null;
    }
    try {
      return await lauf(lease);
    } finally {
      await puffer.lease.gibFrei(lease).catch(() => undefined);
    }
  }
  const vorher = laufend.get(kassenId) ?? Promise.resolve();
  const dieser = vorher.catch(() => undefined).then(() => lauf(undefined));
  laufend.set(kassenId, dieser);
  try {
    return await dieser;
  } finally {
    if (laufend.get(kassenId) === dieser) {
      laufend.delete(kassenId);
    }
  }
}

/**
 * Gepufferte Belege der Kasse an die API übermitteln; erfolgreich übernommene
 * (oder dort schon vorhandene) Belege werden aus dem Puffer entfernt.
 * null, wenn nichts zu tun war bzw. ein anderes Fenster gerade synchronisiert.
 */
export async function synchronisiere(
  api: KassenApi,
  puffer: OfflinePuffer,
  kassenId: string,
  jetzt = new Date(),
  optionen: SyncOptionen = {},
): Promise<SyncErgebnis | null> {
  try {
    return await exklusiv(kassenId, puffer, optionen, (lease) =>
      synchronisiereJetzt(api, puffer, kassenId, jetzt, optionen, lease),
    );
  } catch (e: unknown) {
    // Lease abgelaufen bzw. übernommen: dieser Lauf hat nichts mehr geändert; Antworten, die
    // noch eintreffen, sind dank Vorgangskennung wiederholsicher (N1.7).
    if (e instanceof LeaseVerloren) {
      return null;
    }
    throw e;
  }
}

async function synchronisiereJetzt(
  api: KassenApi,
  puffer: OfflinePuffer,
  kassenId: string,
  jetzt: Date,
  optionen: SyncOptionen,
  lease: PufferLease | undefined,
): Promise<SyncErgebnis | null> {
  const uhr = optionen.uhr ?? Date.now;
  // Chronologisch (die Kette folgt der Übertragungsreihenfolge) – unabhängig vom Puffer.
  const belege = [...(await puffer.alle(kassenId))].sort((a, b) =>
    a.zeitpunkt.localeCompare(b.zeitpunkt),
  );
  const offen = parseAbschluss(await leseAbschlussRoh(puffer, optionen.merker, kassenId));
  // Ohne gepufferte Belege nur dann melden, wenn der Sammelbeleg des Ausfalls noch fehlt.
  if (belege.length === 0 && offen === null) {
    return null;
  }
  // N1: Altbestand ohne Vorgangskennung bekommt vor dem ersten Senden eine (ursprünglicher
  // Zeitpunkt) – dauerhaft gespeichert, damit jede Wiederholung dieselbe trägt.
  for (let i = 0; i < belege.length; i++) {
    const b = belege[i];
    if (b !== undefined && b.vorgang === undefined) {
      const ergaenzt: OfflineBeleg = {
        ...b,
        vorgang: { id: (optionen.neueVorgangsId ?? zufallsId)(), zeitpunkt: b.zeitpunkt },
      };
      await puffer.ergaenze(ergaenzt, lease);
      belege[i] = ergaenzt;
    }
  }
  const setzeAbschluss = async (wert: string): Promise<void> => {
    if (puffer.abschluss !== undefined) {
      await puffer.abschluss.setze(kassenId, wert, lease);
      // Übernommener localStorage-Merker ist damit erledigt (nur noch der geschützte zählt)
      if (optionen.merker?.getItem(abschlussSchluessel(kassenId))) {
        optionen.merker.setItem(abschlussSchluessel(kassenId), '');
      }
    } else {
      optionen.merker?.setItem(abschlussSchluessel(kassenId), wert);
    }
  };
  const stempel = jetzt
    .toISOString()
    .replace(/[-:TZ.]/g, '')
    .slice(0, 14);
  // P1-12: Eine reine Abschluss-Wiederholung nutzt die gemerkte Sammelbelegnummer; mit Belegen
  // gibt es eine neue (der Server schließt ohnehin nie mit einem überholten Sammelbeleg ab).
  const sammelbelegnummer =
    belege.length === 0 && offen?.sammelbelegnummer != null
      ? offen.sammelbelegnummer
      : `SB-${stempel}`;
  const groesse = Math.max(1, Math.min(optionen.stapel ?? SYNC_STAPEL, 500));
  // N1 (Codex-Runde 10): ein unterbrochener Lauf wird mit derselben Laufkennung fortgesetzt –
  // der Server hält die Abschlusswege bis zu seinem abschließenden Stapel gesperrt
  let lauf = offen?.lauf ?? zufallsId();
  // Stop-Prüfung: Laufkennung VOR dem ersten Sendeversuch sichern – geht schon die erste Antwort
  // verloren, setzt der nächste Lauf mit derselben Kennung fort (sonst bliebe das
  // Stapelhindernis des Servers bis zu seinem Ablauf stehen)
  if (belege.length > 0 && offen?.lauf !== lauf) {
    await setzeAbschluss(
      JSON.stringify({ ausfallId: offen?.ausfallId ?? null, sammelbelegnummer, lauf }),
    );
  }
  /**
   * Codex-Runde 13: Meldet der Server die Laufkennung als bereits erledigt (z. B. Antwort des
   * Abschlusses verloren, Betreiber-Freigabe), beginnt die Kasse einen neuen Lauf – Kennung vor
   * dem Senden gesichert – und sendet den Stapel genau einmal erneut (Puffer unverändert).
   */
  const sendeStapel = async <T>(senden: () => Promise<T>): Promise<T> => {
    try {
      return await senden();
    } catch (e: unknown) {
      if (!(e instanceof ApiFehler && e.status === 409 && e.daten?.laufErledigt === true)) {
        throw e;
      }
      lauf = zufallsId();
      await setzeAbschluss(
        JSON.stringify({ ausfallId: offen?.ausfallId ?? null, sammelbelegnummer, lauf }),
      );
      return await senden();
    }
  };
  const ergebnis: SyncErgebnis = {
    synchronisiert: [],
    fehler: [],
    ausfallBeendet: false,
    inKlaerung: [],
    klaerungenOffen: 0,
    ohneRechnung: [],
  };
  // P1-1: in Stapeln, chronologisch; der Sammelbeleg entsteht erst mit dem letzten Stapel
  // (bzw. bei leerem Puffer). Ein Fehler beendet den Lauf – der Rest bleibt im Puffer und folgt
  // im nächsten Lauf (Reihenfolge der Kette). Ein Klärungsfall hält den Lauf nicht an: Er
  // bleibt im Puffer, die übrigen Belege werden übertragen.
  let ab = 0;
  do {
    const stapel = belege.slice(ab, ab + groesse);
    ab += stapel.length;
    const letzter = ab >= belege.length;
    // Lease vor jeder Anfrage verlängern (gilt sie nicht mehr: Lauf endet ohne Änderung)
    if (lease !== undefined) {
      await puffer.lease?.verlaengere(lease, uhr());
    }
    const antwort = await sendeStapel(() =>
      api.offlineSynchronisieren(kassenId, {
        beginn: stapel[0]?.zeitpunkt ?? belege[0]?.zeitpunkt ?? jetzt.toISOString(),
        belege: stapel.map((b) => ({
          belegnummer: b.belegnummer,
          zeitpunkt: b.zeitpunkt,
          positionen: b.positionen,
          zahlungsart: 'bar',
          ...(b.empfaenger === undefined ? {} : { empfaenger: b.empfaenger }),
          ...(b.notiz === undefined ? {} : { notiz: b.notiz }),
          ...(b.bediener === undefined ? {} : { bediener: b.bediener }),
          ...(b.kundeId === undefined ? {} : { kundeId: b.kundeId }),
          ...(b.bedienerSitzung === undefined ? {} : { bedienerSitzung: b.bedienerSitzung }),
          ...(b.gegebenCent === undefined ? {} : { gegebenCent: b.gegebenCent }),
          ...(b.trinkgeldCent === undefined ? {} : { trinkgeldCent: b.trinkgeldCent }),
          ...(b.vorgang === undefined ? {} : { vorgang: b.vorgang }),
        })),
        sammelbelegnummer,
        lauf,
        ...(letzter ? {} : { abschliessen: false }),
        ...(stapel.length === 0 && offen?.ausfallId != null ? { ausfallId: offen.ausfallId } : {}),
      }),
    );
    const synchronisiert = antwort.belege
      .filter((b) => b.status === 'erstellt' || b.status === 'vorhanden')
      .map((b) => b.belegnummer);
    await puffer.entferne(kassenId, synchronisiert, lease);
    ergebnis.synchronisiert.push(...synchronisiert);
    // N1.5: Klärung (bzw. „konflikt“ eines älteren Servers): Eintrag bleibt unverändert – keine
    // automatische Neunummerierung, keine Quittierung.
    ergebnis.inKlaerung.push(
      ...antwort.belege
        .filter((b) => b.status === 'klaerung' || b.status === 'konflikt')
        .map((b) => b.belegnummer),
    );
    ergebnis.fehler.push(
      ...antwort.belege
        .filter((b) => b.status === 'fehler')
        .map((b) => ({ belegnummer: b.belegnummer, fehler: b.fehler ?? 'unbekannt' })),
    );
    ergebnis.ohneRechnung.push(
      ...antwort.belege.flatMap((b) =>
        b.hinweis === undefined ? [] : [{ belegnummer: b.belegnummer, hinweis: b.hinweis }],
      ),
    );
    ergebnis.ausfallBeendet = antwort.ausfallBeendet;
    ergebnis.klaerungenOffen = antwort.klaerungenOffen ?? 0;
    // Ausfallabschluss merken, bis der Server den Ausfall beendet hat (oder keiner mehr läuft) –
    // nach jedem Stapel, damit ein Abbruch zwischen zwei Stapeln nichts verliert. Den Abschluss
    // bestimmt die Serverantwort (offene Klärungen halten ihn offen).
    const ausfallOffen = !antwort.ausfallBeendet && antwort.ausfall != null;
    // Codex-Runde 11: Solange weitere Stapel folgen, bleibt die Laufkennung gesichert – auch ohne
    // aktiven Ausfall (z. B. nur „vorhanden“); sonst ginge sie bei einer Unterbrechung verloren.
    // Codex-Runde 13: Hat der Server den Lauf abgeschlossen, beginnt die nächste Übertragung
    // einen neuen Lauf (auch wenn der Ausfall wegen einer Klärung offen bleibt); meldet er ihn
    // als nicht abgeschlossen, bleibt die Kennung bis zum Abschluss erhalten.
    const laufErledigt = letzter && antwort.laufAbgeschlossen === true;
    await setzeAbschluss(
      ausfallOffen || !letzter || antwort.laufAbgeschlossen === false
        ? JSON.stringify({
            ausfallId: ausfallIdAus(antwort.ausfall),
            sammelbelegnummer,
            lauf: laufErledigt ? null : lauf,
          })
        : '',
    );
    const weiter = antwort.belege.every(
      (b) =>
        b.status === 'erstellt' ||
        b.status === 'vorhanden' ||
        b.status === 'klaerung' ||
        b.status === 'konflikt',
    );
    if (!weiter || antwort.belege.length < stapel.length) {
      break;
    }
  } while (ab < belege.length);
  return ergebnis;
}
